import { Box, Divider, Typography } from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Text,
  XAxis,
  YAxis,
} from 'recharts';

import { AsyncContent } from '~/components/AsyncContent';
import { Enum } from '~/data/enums';
import { formatCurrency, formatNumber } from '~/services/Formatters';
import { People } from '~/services/Http';

import { useSxStyles } from './useStyles';

export interface Props {
  loading: boolean;
  failedToLoad: boolean;
  compensationSummary?: People.CompensationsSummaryDto;
  getSalaryFrequency: (code?: string) => Enum | undefined;
}

export const Content: React.FC<Props> = ({
  loading,
  failedToLoad,
  compensationSummary,
  getSalaryFrequency,
}) => {
  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  if (!compensationSummary || !compensationSummary.current) {
    return (
      <AsyncContent
        isLoading={false}
        failedToLoad={!compensationSummary}
        failedToLoadMessage={t('infoNotAvailable')}
        noData={!compensationSummary?.current}
        noDataMessage={t('noCurrentSalaryOnEmployment')}
      >
        <></>
      </AsyncContent>
    );
  }

  const { current, history } = compensationSummary;

  const latestCompensationCurrency = formatCurrency(current.currency);

  return (
    <AsyncContent isLoading={loading} failedToLoad={failedToLoad}>
      <Box sx={sxStyles.miniTableRow}>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={sxStyles.miniTableAttribute}
        >
          {t('baseSalary')}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={sxStyles.miniTableAttribute}
        >
          {t('frequency')}
        </Typography>
      </Box>
      <Divider sx={sxStyles.divider} />
      <Box sx={sxStyles.miniTableRow}>
        <Typography sx={sxStyles.miniTableValue} data-cy="base-salary">
          {`${formatNumber(
            current.baseSalary,
            2,
          )}${latestCompensationCurrency}`}
        </Typography>
        <Typography sx={sxStyles.miniTableValue} data-cy="frequency">
          {getSalaryFrequency(current.salaryFrequency)?.name}
        </Typography>
      </Box>

      <Box
        sx={sxStyles.responsiveContainerContainer}
        data-cy="base-salary-graph"
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={history.map((c) => ({
              baseSalary: c.baseSalary,
              startDate: moment(c.startDate).valueOf(),
            }))}
            margin={{
              top: 16,
              bottom: 16,
              right: 4,
            }}
          >
            <YAxis
              dataKey="baseSalary"
              domain={['dataMin', 'auto']}
              tick={(props) => (
                <Text {...props} fontSize="0.75rem">
                  {props.payload.value}
                </Text>
              )}
              tickLine={false}
              tickCount={4}
              interval={0}
              width={36}
            />
            <XAxis
              hide={true}
              type="number"
              dataKey="startDate"
              domain={['dataMin', 'dataMax']}
              tickCount={4}
              interval={0}
            />
            <CartesianGrid stroke="#eee" />
            <Line type="linear" dataKey="baseSalary" stroke="#FFD78F" />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </AsyncContent>
  );
};
